import * as React from "react";
import styled from "styled-components";
import { useAuth } from '../../contexts/AuthContext';
import { usePrivacy } from '../../contexts/PrivacyContext';

const UserAvatar = ({ initials, backgroundColor, isVisible, name }) => (
    <AvatarCircle $backgroundColor={backgroundColor} $isVisible={isVisible} data-tooltip={name}>
        {initials}
    </AvatarCircle>
);

const ParticipantList = ({ session }) => {
    const { groupMembers } = useAuth();
    const { maskName, maskInitial } = usePrivacy();

    return (
        <UserListWrapper>
            <UserItem>
                <ContributorsWrapper>
                    {groupMembers.map((user, index) => {
                        const isContributor = session.contributors.some(contributor =>
                            contributor.email === user.email
                        );
                        return (
                            <UserAvatar
                                key={index}
                                initials={maskInitial(user.name) || user.initial}
                                backgroundColor={user.color || '#cccccc'}
                                isVisible={isContributor}
                                name={maskName(user.name || user.initial)}
                            />
                        );
                    })}
                </ContributorsWrapper>
            </UserItem>
        </UserListWrapper>
    );
};

const UserListWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-height: 22px;
    padding-bottom: 5px;
`;

const UserItem = styled.div`
    display: flex;
    align-items: center;
`;

const ContributorsWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    margin-right: 8px;
    line-height: 1;
`;

const AVATAR_SIZE = 23;

const AvatarCircle = styled.div`
    display: inline-flex;
    align-items: center;
    line-height: 1;   
    position: relative;  
    justify-content: center;
    width: ${AVATAR_SIZE}px;
    height: ${AVATAR_SIZE}px;
    border-radius: 9999px;
    padding-top: 1px;
    background-color: ${props => props.$backgroundColor};
    border: 1px solid ${props => props.$backgroundColor};         /* subtle, neutral border like a chip */
    color: #111827;
    font-size: 11px;                   /* balanced inside 20px circle */
    font-weight: 500;
    letter-spacing: .2px;
    vertical-align: middle;            /* baseline-align with Tag */
    margin-right: 0;                   /* spacing controlled by gap above */
    opacity: ${props => (props.$isVisible ? 1 : 0)};
    visibility: ${props => (props.$isVisible ? 'visible' : 'hidden')};
    transition: opacity .2s ease, visibility .2s ease, transform .15s ease, box-shadow .15s ease;

    /* subtle affordance without breaking minimalism */
    /* custom tooltip base (hidden by default) */
    &::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%);
        background: #111827;
        color: #fff;
        font: 600 12px/1.2 Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
        padding: 6px 8px;
        border-radius: 6px;
        white-space: nowrap;
        box-shadow: 0 6px 18px rgba(0,0,0,.15);
        z-index: 20;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition: opacity .15s ease, visibility .15s ease;
    }

    &::before {
        content: "";
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: #111827;
        z-index: 19;
        opacity: 0;
        visibility: hidden;
        transition: opacity .15s ease, visibility .15s ease;
    }

    /* show tooltip on hover when data-tooltip exists */
    &[data-tooltip]:hover::after,
    &[data-tooltip]:hover::before {
        opacity: 1;
        visibility: visible;
    }

    &:hover {
        transform: translateY(-1px);
    }
    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.28);
    }
    `;

export default ParticipantList;
